import React, { useCallback, useEffect, useMemo, useState } from "react";
import { capitalizeWords } from "utils/text.js";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  BadgeInfo,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  ExternalLink,
  Eye,
  Gift,
  KeyRound,
  Mail,
  MonitorSmartphone,
  ReceiptText,
  ShieldCheck,
  User,
  Crown,
} from "lucide-react";

import BackdropAlert from "components/Custom/BackdropAlert.js";
import { supabase } from "utils/supabase";

const dateFormatter = new Intl.DateTimeFormat("es-MX", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const currencyFormatter = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  maximumFractionDigits: 2,
});

const toDateLabel = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return dateFormatter.format(parsed);
};

const toMoneyLabel = (value) => {
  if (value === null || value === undefined) return "-";
  return currencyFormatter.format(Number(value));
};

const iconMap = {
  user: User,
  mail: Mail,
  "credit-card": CreditCard,
  "shield-check": ShieldCheck,
  gift: Gift,
  info: BadgeInfo,
};

const indigoButtonSx = {
  backgroundColor: "#4f46e5",
  color: "#fff",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#4338ca",
    boxShadow: "none",
  },
};

const indigoOutlinedButtonSx = {
  borderColor: "#4f46e5",
  color: "#4f46e5",
  "&:hover": {
    borderColor: "#4338ca",
    backgroundColor: "rgba(79, 70, 229, 0.08)",
  },
};

const indigoTextButtonSx = {
  color: "#4f46e5",
  "&:hover": {
    backgroundColor: "rgba(79, 70, 229, 0.08)",
  },
};

const getPasswordValidation = (password) => {
  const value = password || "";

  const checks = {
    minLength: value.length >= 8,
    hasUppercase: /[A-Z]/.test(value),
    hasLowercase: /[a-z]/.test(value),
    hasNumber: /\d/.test(value),
    hasSymbol: /[^A-Za-z0-9]/.test(value),
    hasNoSpaces: !/\s/.test(value),
  };

  return {
    ...checks,
    isValid: Object.values(checks).every(Boolean),
  };
};

function SectionCard({ icon: Icon, title, action, children, sx }) {
  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "rgba(99, 102, 241, 0.14)",
        borderRadius: 4,
        boxShadow: "0 18px 55px rgba(15, 23, 42, 0.06)",
        overflow: "hidden",
        position: "relative",
        ...sx,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(249,250,255,0.96) 100%)",
        }}
      />
      <CardContent sx={{ p: { xs: 2, md: 2.5 }, position: "relative" }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ width: "100%", mb: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2.25,
                display: "grid",
                placeItems: "center",
                color: "white",
                background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                boxShadow: "0 10px 24px rgba(79, 70, 229, 0.24)",
                flexShrink: 0,
              }}
            >
              <Icon size={18} />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", minHeight: 36 }}>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                {title}
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ display: "flex", justifyContent: "flex-end", flexShrink: 0, ml: "auto" }}>
            {action}
          </Box>
        </Stack>

        {children}
      </CardContent>
    </Card>
  );
}

function FieldPair({ label, value, align = "left" }) {
  return (
    <Box sx={{ textAlign: align }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
        {value}
      </Typography>
    </Box>
  );
}

function AccountInfoTile({ label, value, icon: Icon }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "rgba(148, 163, 184, 0.25)",
        bgcolor: "rgba(248, 250, 252, 0.85)",
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 999,
            display: "grid",
            placeItems: "center",
            bgcolor: "white",
            color: "primary.main",
            border: "1px solid",
            borderColor: "rgba(79, 70, 229, 0.15)",
            flexShrink: 0,
          }}
        >
          <Icon size={14} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {label}
          </Typography>
          <Typography variant="body2" fontWeight={700} sx={{ wordBreak: "break-word" }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

function SessionRow({ session }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: session.is_current_device ? "success.100" : "rgba(148, 163, 184, 0.28)",
        bgcolor: session.is_current_device ? "success.50" : "rgba(248, 250, 252, 0.75)",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
            <Typography variant="body1" fontWeight={800}>
              {session.device_name || "Dispositivo"}
            </Typography>
            {session.is_current_device ? <Chip size="small" label="Este dispositivo" color="success" /> : null}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Última actividad: {toDateLabel(session.last_seen_at)}
          </Typography>
        </Box>

        <Chip
          size="small"
          label={session.status || "active"}
          color={session.is_current_device ? "success" : "default"}
          icon={<CheckCircle2 size={14} />}
        />
      </Stack>
    </Box>
  );
}

function InvoiceTable({ invoices }) {
  const getStatusColor = (status) => {
    if (status === "paid") return "success";
    if (status === "open" || status === "draft") return "warning";
    return "default";
  };

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 720 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Descripción</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Monto</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Acción
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoices.map((invoice) => {
            const invoiceHref = invoice.hosted_invoice_url || invoice.invoice_pdf || "#";

            return (
              <TableRow key={invoice.id} hover>
                <TableCell>{toDateLabel(invoice.date)}</TableCell>
                <TableCell>
                  <Stack spacing={0.25}>
                    <Typography variant="body2" fontWeight={700}>
                      {invoice.description || invoice.number || "-"}
                    </Typography>
                    {invoice.number ? (
                      <Typography variant="caption" color="text.secondary">
                        {invoice.number}
                      </Typography>
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>{invoice.amount_label || toMoneyLabel(invoice.amount_paid / 100)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={invoice.status_label || invoice.status || "-"}
                    color={getStatusColor(invoice.status)}
                  />
                </TableCell>
                <TableCell align="right">
                  <Button
                    component="a"
                    href={invoiceHref}
                    target="_blank"
                    rel="noreferrer"
                    size="small"
                    variant="text"
                    sx={indigoTextButtonSx}
                    endIcon={<Eye size={14} />}
                  >
                    Ver en Stripe
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

function BillingBanner({ onOpenPortal }) {
  return (
    <Box
      sx={{
        width: "100%",
        display: "block",
        p: { xs: 1.5, md: 2 },
        borderRadius: 3.5,
        border: "1px solid",
        borderColor: "rgba(99, 102, 241, 0.18)",
        background:
          "radial-gradient(circle at top left, rgba(99, 102, 241, 0.08), transparent 30%), linear-gradient(180deg, rgba(237, 242, 255, 0.98) 0%, rgba(248, 250, 255, 0.99) 100%)",
        mt: 1.5,
        mb: 2,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        sx={{ width: "100%", flex: 1 }}
      >
        <Stack direction="row" spacing={1.75} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 86,
              height: 44,
              display: "grid",
              placeItems: "center",
              color: "#635bff",
              fontWeight: 900,
              fontSize: 32,
              letterSpacing: -1.3,
              flexShrink: 0,
            }}
          >
            stripe
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body1" fontWeight={800}>
              Tus recibos y facturas se gestionan desde Stripe.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Para ver, descargar o solicitar facturas, accede al portal de facturación.
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", justifyContent: "flex-end", flexShrink: 0 }}>
          <Button variant="contained" sx={indigoButtonSx} endIcon={<ExternalLink size={16} />} onClick={onOpenPortal}>
            Abrir portal de facturación
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

export default function MiCuenta() {
  const [loading, setLoading] = useState(true);
  const [accountData, setAccountData] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [message, setMessage] = useState("");
  const [messageSeverity, setMessageSeverity] = useState("info");
  const [cancelBackdropOpen, setCancelBackdropOpen] = useState(false);
  const [cancelBackdropLoading, setCancelBackdropLoading] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordConfirmStep, setPasswordConfirmStep] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordModalError, setPasswordModalError] = useState("");

  const loadAccountPage = useCallback(async () => {
    setLoading(true);
    setMessage("");

    const deviceId = localStorage.getItem("enarm_pro_device_id");

    const [accountResult, billingResult] = await Promise.all([
      supabase.rpc("get_account_page_data", {
        p_device_id: deviceId,
      }),
      supabase.functions.invoke("get-stripe-billing-summary"),
    ]);

    if (accountResult.error) {
      console.error("Error account:", accountResult.error);
      setMessage("No se pudo cargar la información de cuenta.");
      setMessageSeverity("error");
    }

    if (billingResult.error) {
      console.error("Error billing:", billingResult.error);
      setMessage("No se pudo cargar la información de facturación.");
      setMessageSeverity("error");
    }

    setAccountData(accountResult.data || null);
    setBillingData(billingResult.data || null);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadAccountPage();
  }, [loadAccountPage]);

  const openBillingPortal = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke("create-customer-portal-session");

    if (error) {
      console.error(error);
      setMessage("No se pudo abrir el portal de facturación.");
      setMessageSeverity("error");
      return;
    }

    if (data?.url) window.location.href = data.url;
  }, []);

  const startCheckout = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke("create-checkout-session");

    if (error) {
      console.error(error);
      setMessage("No se pudo iniciar el proceso de compra.");
      setMessageSeverity("error");
      return;
    }

    if (data?.url) {
      window.location.href = data.url;
      return;
    }

    if (data?.redirect_to) {
      window.location.href = data.redirect_to;
    }
  }, []);

  const cancelSubscription = useCallback(async () => {
    setCancelBackdropLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("cancel-subscription");

      if (error) {
        console.error(error);
        setMessage("No se pudo cancelar la suscripción.");
        setMessageSeverity("error");
        return;
      }

      if (data?.success) {
        setMessage("La renovación de la suscripción fue cancelada.");
        setMessageSeverity("success");
        setCancelBackdropOpen(false);
        await loadAccountPage();
      }
    } finally {
      setCancelBackdropLoading(false);
    }
  }, [loadAccountPage]);

  const profile = accountData?.profile;
  const subscription = accountData?.subscription;
  const accountInfo = accountData?.account_info || [];
  const sessions = accountData?.device_sessions?.active_sessions || [];
  const actions = useMemo(() => accountData?.actions || {}, [accountData?.actions]);

  const paymentMethod = billingData?.payment_method;
  const invoices = billingData?.invoices || [];

  const isTrial = Boolean(subscription?.is_trial);
  const isCanceledAtPeriodEnd = Boolean(subscription?.cancel_at_period_end);
  const isSubscriptionCanceled = Boolean(subscription?.status === "canceled" || subscription?.status === "cancelled");
  const canManageAppleSubscription = Boolean(
    actions?.can_manage_apple_subscription &&
      (subscription?.subscription_provider === "apple" || subscription?.apple?.management_provider === "apple")
  );
  const hasActivePro = Boolean(subscription?.has_pro_access);
  const hasStripeCustomer = Boolean(billingData?.stripe_customer_exists || subscription?.stripe_customer_id_exists);
  const passwordValidation = useMemo(() => getPasswordValidation(newPassword), [newPassword]);
  const passwordsMatch = newPassword === confirmNewPassword;
  const shouldShowPasswordRules = Boolean(newPassword);

  const primaryPlanActionLabel = useMemo(() => {
    if (!subscription || subscription.status === "free") return "Explorar planes";
    return "Actualizar plan";
  }, [subscription]);

  const primaryPlanActionDisabled =
    (!hasStripeCustomer && !actions?.can_start_checkout) ||
    (subscription?.status !== "free" && !actions?.can_change_plan && !actions?.can_open_billing_portal);

  const handlePrimaryPlanAction = useCallback(() => {
    if (!subscription || subscription.status === "free") {
      if (actions?.can_start_checkout) {
        startCheckout();
        return;
      }

      openBillingPortal();
      return;
    }

    openBillingPortal();
  }, [actions, openBillingPortal, startCheckout, subscription]);

  const handleOpenCancelBackdrop = useCallback(() => {
    setCancelBackdropOpen(true);
  }, []);

  const handleCloseCancelBackdrop = useCallback(() => {
    if (cancelBackdropLoading) return;
    setCancelBackdropOpen(false);
  }, [cancelBackdropLoading]);

  const handleConfirmCancelSubscription = useCallback(() => {
    if (canManageAppleSubscription) {
      window.location.href = "https://apps.apple.com/account/subscriptions";
      return;
    }

    cancelSubscription();
  }, [canManageAppleSubscription, cancelSubscription]);

  const handleOpenPasswordModal = useCallback(() => {
    setPasswordModalError("");
    setPasswordConfirmStep(false);
    setPasswordModalOpen(true);
  }, []);

  const handleClosePasswordModal = useCallback(() => {
    if (changePasswordLoading) return;
    setPasswordModalError("");
    setPasswordConfirmStep(false);
    setPasswordModalOpen(false);
    setNewPassword("");
    setConfirmNewPassword("");
  }, [changePasswordLoading]);

  const handleRequestPasswordChange = useCallback(() => {
    const candidatePassword = newPassword;
    setPasswordModalError("");

    if (!candidatePassword || !confirmNewPassword) {
      setPasswordModalError("Completa ambos campos de contraseña.");
      return;
    }

    if (!passwordValidation.isValid) {
      setPasswordModalError("La contraseña debe tener al menos 8 caracteres, incluir mayúscula, minúscula, número, símbolo y no contener espacios.");
      return;
    }

    if (!passwordsMatch) {
      setPasswordModalError("Las contraseñas no coinciden.");
      return;
    }

    setPasswordConfirmStep(true);
  }, [confirmNewPassword, newPassword, passwordValidation.isValid, passwordsMatch]);

  const handleConfirmPasswordChange = useCallback(async () => {
    setChangePasswordLoading(true);
    setPasswordModalError("");

    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });

      if (error) {
        console.error(error);
        if (error.message === "New password should be different from the old password.") {
          setPasswordModalError("La nueva contraseña debe ser diferente a la contraseña actual.");
          return;
        }

        setPasswordModalError(error.message || "No se pudo actualizar la contraseña.");
        return;
      }

      setMessage("Tu contraseña se actualizó correctamente.");
      setMessageSeverity("success");
      setPasswordConfirmStep(false);
      setPasswordModalOpen(false);
      setNewPassword("");
      setConfirmNewPassword("");
    } finally {
      setChangePasswordLoading(false);
    }
  }, [newPassword]);

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100%",
        background:
          "radial-gradient(circle at top left, rgba(99, 102, 241, 0.06), transparent 26%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.05), transparent 24%), linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
      }}
    >
    <Container maxWidth="xl" sx={{ py: 3, position: "relative", zIndex: 1 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: -0.5 }}>
            Mi cuenta
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Perfil, suscripción, facturación y seguridad en un solo lugar.
          </Typography>
        </Box>
      </Stack>

      {message ? (
        <Alert severity={messageSeverity} sx={{ mb: 2, borderRadius: 2.5 }}>
          {message}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) minmax(0, 1fr)" },
          gap: 2.25,
          alignItems: "start",
        }}
      >
        <Box sx={{ display: "grid", gap: 2.25 }}>
          <SectionCard
            icon={User}
            title="Perfil"
            /*action={
              <Button size="small" variant="outlined" sx={indigoOutlinedButtonSx} startIcon={<PencilLine size={16} />} disabled>
                Editar perfil
              </Button>
            }*/
          >
            {loading ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <Skeleton variant="circular" width={64} height={64} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="42%" />
                  <Skeleton width="58%" />
                  <Skeleton width="28%" />
                </Box>
              </Stack>
            ) : (
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={profile?.avatar_url || ""}
                  alt={capitalizeWords(profile?.full_name) || "Perfil"}
                  sx={{ width: 64, height: 64, boxShadow: "0 12px 30px rgba(15, 23, 42, 0.12)", border: "2px solid rgba(255,255,255,0.9)" }}
                >
                  {(capitalizeWords(profile?.full_name) || "A").slice(0, 1)}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1.15 }}>
                    {capitalizeWords(profile?.full_name) || "Sin nombre"}
                  </Typography>

                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <Mail size={16} />
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {profile?.email || "-"}
                    </Typography>
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {profile?.role_label || "-"}
                  </Typography>
                </Box>
              </Stack>
            )}
          </SectionCard>

          <SectionCard
            icon={CreditCard}
            title="Método de pago"
            action={
              <Button size="small" variant="outlined" sx={indigoOutlinedButtonSx} onClick={openBillingPortal} disabled={!hasStripeCustomer}>
                Actualizar
              </Button>
            }
          >
            {loading ? (
              <Stack spacing={1}>
                <Skeleton width="55%" />
                <Skeleton width="38%" />
              </Stack>
            ) : paymentMethod ? (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: "fit-content",
                    minWidth: 58,
                    height: 36,
                    borderRadius: 1.75,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    placeItems: "center",
                    px: 1.25,
                    border: "1px solid rgba(99, 102, 241, 0.18)",
                    bgcolor: "rgba(255,255,255,0.85)",
                    textTransform: "uppercase",
                    fontSize: 11.5,
                    fontWeight: 900,
                    letterSpacing: 0.7,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.45)",
                  }}
                >
                  {String(paymentMethod.brand || paymentMethod.type || "card").toUpperCase()}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1" fontWeight={800}>
                    {paymentMethod.label || `Tarjeta terminación ${paymentMethod.last4}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Expira {paymentMethod.expires_label || "-"}
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Stack spacing={1.25}>
                <Typography variant="body1" fontWeight={800}>
                  Sin método de pago registrado
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Puedes agregar uno desde el portal de Stripe.
                </Typography>
                <Box>
                  <Button variant="outlined" sx={indigoOutlinedButtonSx} onClick={openBillingPortal} disabled={!hasStripeCustomer}>
                    Abrir portal de facturación
                  </Button>
                </Box>
              </Stack>
            )}
          </SectionCard>
        </Box>

        <Box>
          <SectionCard
            icon={Crown}
            title="Suscripción"
            action={
              <Chip
                label={subscription?.status_label || "Sin estado"}
                color={subscription?.status === "active" ? "success" : subscription?.status === "free" ? "default" : "warning"}
                size="small"
              />
            }
            sx={{ height: "100%" }}
          >
            {loading ? (
              <Stack spacing={1.25}>
                <Skeleton width="60%" />
                <Skeleton width="36%" />
                <Skeleton width="88%" />
              </Stack>
            ) : (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FieldPair label="Plan" value={subscription?.plan_label || "Sin plan"} />
                  </Grid>
                  <Grid item xs={6}>
                    <FieldPair label="Precio" value={subscription?.price_label || "$0 MXN"} align="right" />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 1.75 }} />

                {isTrial ? (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2.5,
                      border: "1px solid",
                      borderColor: "success.100",
                      bgcolor: "success.50",
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Gift size={16} />
                      <Box>
                        <Typography variant="body2" fontWeight={800}>
                          Periodo de prueba activo
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {subscription?.trial_days_left || 0} días restantes
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                ) : null}

                {hasActivePro ? (
                  <Box sx={{ mt: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                      <CalendarDays size={16} />
                      <Typography variant="body2" color="text.secondary">
                        {isCanceledAtPeriodEnd
                          ? `Suscripción activa hasta el ${toDateLabel(subscription?.current_period_end || subscription?.renewal_date)}.`
                          : `Próxima renovación: ${toDateLabel(subscription?.renewal_date)}`}
                      </Typography>
                    </Stack>

                    {isCanceledAtPeriodEnd ? (
                      <Typography variant="body2" fontWeight={800}>
                        La renovación está cancelada.
                      </Typography>
                    ) : null}
                  </Box>
                ) : (
                  <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                    <BadgeInfo size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Sin suscripción activa.
                    </Typography>
                  </Box>
                )}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} mt={2.25}>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={indigoOutlinedButtonSx}
                    onClick={handlePrimaryPlanAction}
                    disabled={primaryPlanActionDisabled}
                  >
                    {primaryPlanActionLabel}
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    onClick={handleOpenCancelBackdrop}
                    disabled={(!actions?.can_cancel_subscription && !canManageAppleSubscription) || isCanceledAtPeriodEnd || isSubscriptionCanceled}
                  >
                    Cancelar suscripción
                  </Button>
                </Stack>
              </Box>
            )}
          </SectionCard>
        </Box>

      </Box>

      <Box sx={{ mt: 4, width: "100%", display: "grid", gap: 2.25 }}>
        <SectionCard icon={ReceiptText} title="Facturas y pagos" sx={{ width: "100%" }}>
            <BillingBanner onOpenPortal={openBillingPortal} />

            {loading ? (
              <Skeleton variant="rounded" height={124} />
            ) : invoices.length ? (
              <Box sx={{ border: "1px solid", borderColor: "rgba(148, 163, 184, 0.22)", borderRadius: 3, overflow: "hidden" }}>
                <InvoiceTable invoices={invoices} />
              </Box>
            ) : (
              <Stack spacing={1.25}>
                <Typography variant="body1" fontWeight={800}>
                  Sin facturas todavía
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No hay registros de facturación disponibles.
                </Typography>
                <Box>
                  <Button variant="outlined" sx={indigoOutlinedButtonSx} onClick={openBillingPortal} disabled={!hasStripeCustomer}>
                    Ver todas las facturas
                  </Button>
                </Box>
              </Stack>
            )}
        </SectionCard>

        <SectionCard icon={ShieldCheck} title="Seguridad y preferencias" sx={{ width: "100%" }}>
            {loading ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <Skeleton variant="rounded" height={112} />
                </Grid>
                <Grid item xs={12} md={7}>
                  <Skeleton variant="rounded" height={112} />
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <Box
                    component="button"
                    type="button"
                    onClick={handleOpenPasswordModal}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: "1px solid rgba(148, 163, 184, 0.22)",
                      height: "100%",
                      bgcolor: "rgba(248, 250, 252, 0.72)",
                      width: "100%",
                      textAlign: "left",
                      cursor: "pointer",
                      transition: "all .18s ease",
                      "&:hover": {
                        borderColor: "rgba(79, 70, 229, 0.38)",
                        bgcolor: "rgba(238, 242, 255, 0.78)",
                      },
                      "&:focus-visible": {
                        outline: "2px solid rgba(79, 70, 229, 0.45)",
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <KeyRound size={18} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={800}>
                          Cambiar contraseña
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Actualiza tu contraseña periódicamente para mantener tu cuenta segura.
                        </Typography>
                      </Box>
                      <ChevronRight size={18} />
                    </Stack>
                  </Box>
                </Grid>

                <Grid item xs={12} md={7}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: "1px solid rgba(148, 163, 184, 0.22)",
                      bgcolor: "rgba(248, 250, 252, 0.72)",
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center" mb={1.5}>
                      <MonitorSmartphone size={18} />
                      <Typography variant="subtitle1" fontWeight={800}>
                        Sesiones activas
                      </Typography>
                    </Stack>

                    {sessions.length ? (
                      <Stack spacing={1.25}>
                        {sessions.map((session) => (
                          <SessionRow key={session.id} session={session} />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No hay sesiones activas.
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={1.5}>
              {accountInfo.map((item) => {
                const InfoIcon = iconMap[item.icon] || BadgeInfo;

                return (
                  <Grid item xs={12} sm={6} md={4} key={item.label}>
                    <AccountInfoTile label={item.label} value={item.value} icon={InfoIcon} />
                  </Grid>
                );
              })}
            </Grid>
        </SectionCard>

        {!loading && !hasStripeCustomer ? (
          <Card
            elevation={0}
            sx={{
              width: "100%",
              border: "1px solid",
              borderColor: "rgba(148, 163, 184, 0.22)",
              borderRadius: 4,
              bgcolor: "rgba(248, 250, 252, 0.95)",
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="h6" fontWeight={900}>
                    Sin plan activo
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Puedes explorar planes y continuar con el proceso de compra.
                  </Typography>
                </Box>

                <Button variant="contained" sx={indigoButtonSx} onClick={handlePrimaryPlanAction} disabled={primaryPlanActionDisabled}>
                  {primaryPlanActionLabel}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ) : null}
      </Box>

      <BackdropAlert
        open={cancelBackdropOpen}
        onClose={handleCloseCancelBackdrop}
        severity="danger"
        title={canManageAppleSubscription ? "Administrar suscripción de Apple" : "Cancelar suscripción"}
        message={
          canManageAppleSubscription
            ? "Para cancelar la renovación, abre la gestión de suscripciones de tu cuenta de Apple."
            : "¿Quieres cancelar la renovación de tu suscripción?"
        }
        description={
          canManageAppleSubscription
            ? "La cancelación debe hacerse directamente con Apple. Tu acceso seguirá activo hasta el final del periodo actual."
            : "La suscripción seguirá activa hasta el final del periodo actual. Después de eso, no se renovará automáticamente."
        }
        closeOnBackdropClick={!cancelBackdropLoading}
        actions={[
          {
            key: "cancel",
            label: "Volver",
            onClick: handleCloseCancelBackdrop,
            variant: "outlined",
            color: "indigo",
            disabled: cancelBackdropLoading,
          },
          {
            key: "confirm-cancel",
            label: canManageAppleSubscription
              ? "Abrir suscripciones de Apple"
              : cancelBackdropLoading
                ? "Cancelando..."
                : "Cancelar suscripción",
            onClick: handleConfirmCancelSubscription,
            variant: "contained",
            color: "error",
            disabled: cancelBackdropLoading,
          },
        ]}
      />

      <Dialog
        open={passwordModalOpen}
        onClose={handleClosePasswordModal}
        fullWidth
        maxWidth="xs"
        slotProps={{ paper: { sx: { borderRadius: 4 } } }}
      >
        <DialogTitle sx={{ fontWeight: 400 }}>Actualizar contraseña</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {passwordModalError ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {passwordModalError}
              </Alert>
            ) : null}

            <Typography variant="body2" color="text.secondary">
              Ingresa tu nueva contraseña y confírmala para continuar.
            </Typography>

            {passwordConfirmStep ? (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                Confirma el cambio para actualizar tu contraseña.
              </Alert>
            ) : null}

            <TextField
              label="Nueva contraseña"
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              variant="outlined"
              fullWidth
              autoFocus
              disabled={changePasswordLoading}
              sx={{
                "& .MuiInputLabel-root": {
                  px: 0.6,
                  bgcolor: "background.paper",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#4f46e5",
                  px: 0.6,
                  bgcolor: "background.paper",
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#4f46e5",
                },
              }}
              helperText={
                shouldShowPasswordRules
                  ? "Debe tener 8+ caracteres, mayúscula, minúscula, número, símbolo y sin espacios."
                  : " "
              }
            />

            <TextField
              label="Confirmar contraseña"
              type="password"
              value={confirmNewPassword}
              onChange={(event) => setConfirmNewPassword(event.target.value)}
              variant="outlined"
              fullWidth
              disabled={changePasswordLoading}
              sx={{
                "& .MuiInputLabel-root": {
                  px: 0.6,
                  bgcolor: "background.paper",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#4f46e5",
                  px: 0.6,
                  bgcolor: "background.paper",
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#4f46e5",
                },
              }}
              error={Boolean(confirmNewPassword) && !passwordsMatch}
              helperText={
                confirmNewPassword && !passwordsMatch
                  ? "Las contraseñas deben coincidir."
                  : " "
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            variant="text"
            sx={indigoTextButtonSx}
            onClick={passwordConfirmStep ? () => setPasswordConfirmStep(false) : handleClosePasswordModal}
            disabled={changePasswordLoading}
          >
            {passwordConfirmStep ? "Editar" : "Cancelar"}
          </Button>
          <Button
            variant="contained"
            sx={passwordConfirmStep ? { ...indigoButtonSx, backgroundColor: "#d97706", "&:hover": { backgroundColor: "#b45309", boxShadow: "none" } } : indigoButtonSx}
            onClick={passwordConfirmStep ? handleConfirmPasswordChange : handleRequestPasswordChange}
            disabled={changePasswordLoading}
          >
            {changePasswordLoading ? "Actualizando..." : passwordConfirmStep ? "Confirmar cambio" : "Actualizar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </Box>
  );
}